import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import { Trash2 } from "react-feather";
import Table from "../../core/pagination/datatable";
import PageHeader from "../components/PageHeader";
import OpeningStockModal from "../../core/modals/stock/OpeningStockModal";
import { fetchOpeningStocks, deleteOpeningStockThunk } from "../../core/redux/slices/purchaseSlice";

const fmt = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;
const fmtDate = (v) => v ? new Date(v).toLocaleDateString("en-IN") : "—";

const OpeningStock = () => {
  const dispatch = useDispatch();
  const { openingStock, loading } = useSelector((s) => s.purchases);
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);

  const load = useCallback(() => { dispatch(fetchOpeningStocks()); }, [dispatch]);
  useEffect(() => { load(); }, [load]);

  const data = useMemo(() => {
    const q = search.toLowerCase();
    return openingStock.filter(
      (r) => !q
        || r.entryNo?.toLowerCase().includes(q)
        || r.remarks?.toLowerCase().includes(q)
    );
  }, [openingStock, search]);

  const handleDelete = (record) => {
    const id = record.openingStockID ?? record.id;
    Swal.fire({
      title: "Delete Opening Stock Entry?",
      text: `Entry ${record.entryNo || id} will be removed and product stock quantities reversed.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete",
      confirmButtonColor: "#d33",
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      try {
        await dispatch(deleteOpeningStockThunk(id)).unwrap();
        Swal.fire({ toast: true, position: "top-end", icon: "success", title: "Entry deleted", showConfirmButton: false, timer: 2000 });
      } catch {
        Swal.fire("Error", "Failed to delete opening stock entry.", "error");
      }
    });
  };

  const columns = [
    {
      title: "Entry No", dataIndex: "entryNo", width: 160,
      sorter: (a, b) => (a.entryNo || "").localeCompare(b.entryNo || ""),
    },
    {
      title: "Date", dataIndex: "entryDate", width: 120,
      render: fmtDate,
      sorter: (a, b) => (a.entryDate || "").localeCompare(b.entryDate || ""),
    },
    {
      title: "Total Value (₹)", dataIndex: "totalAmount", align: "right", width: 150,
      render: fmt,
      sorter: (a, b) => (a.totalAmount || 0) - (b.totalAmount || 0),
    },
    { title: "Remarks", dataIndex: "remarks", ellipsis: true },
    {
      title: "", dataIndex: "action", width: 60, align: "center",
      render: (_, record) => (
        <button className="btn btn-sm text-danger p-1" title="Delete" onClick={() => handleDelete(record)}>
          <Trash2 size={15} />
        </button>
      ),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <PageHeader title="Opening Stock" subtitle="Record initial stock quantities for products" onAdd={() => setShowModal(true)} addLabel="Add Opening Stock" />
        <div className="card table-list-card">
          <div className="card-body">
            <div className="table-top d-flex gap-2 flex-wrap align-items-center mb-2">
              <div className="search-input flex-grow-1">
                <input type="text" placeholder="Search entry no or remarks..."
                  className="form-control form-control-sm formsearch"
                  value={search} onChange={(e) => setSearch(e.target.value)} />
              </div>
              <button className="btn btn-sm btn-outline-secondary" onClick={load}>Refresh</button>
            </div>
            <div className="table-responsive">
              <Table columns={columns} dataSource={data} loading={loading} />
            </div>
          </div>
        </div>
      </div>
      <OpeningStockModal show={showModal} onClose={() => { setShowModal(false); load(); }} />
    </div>
  );
};

export default OpeningStock;
